// components/RelatedFollowModal/index.tsx
// 関連フォローモーダル
//
// 機能概要:
//   - 一つのフォローに紐づく「関連フォロー群」をタイムライン形式で閲覧・操作する
//   - スマホ縦向き前提のUX（タイムラインチップ + 上下ナビ + スクロール詳細）
//   - PC では左リスト / 右詳細の2カラムレイアウト（TODO: 本実装時にブレークポイント対応を追加すること）
//
// 保留（今回スコープ外）:
//   - 編集: FollowFormModal を開いてフォロー内容を更新すること（詳細未ロード保存によるデータ欠落を
//     避けるため保留。下部アクションボタンの TODO コメント参照）
//
// コメントCRUD:
//   実体は親（useRelatedFollowActions）が useFollowComments フックで保持し、現在選択中の
//   関連フォロー（onCurrentFollowChange で通知した followNo）に追従させる。このコンポーネント自身は
//   comments（＝ current.comments。onCommentsChange で親が同期済み）をそのまま表示するだけの薄い層。

import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Divider, Typography, Empty, Spin } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  PaperClipOutlined,
  DisconnectOutlined,
  PlusOutlined,
  UserOutlined,
  CalendarOutlined,
  BankOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import LinkFollowModal from '../LinkFollowModal';
import CommentSection from '../CommentSection';
import { MODAL_Z } from '../../utils/modalZIndex';
import { FollowComment } from '../../types/follow';

const { Text } = Typography;

// ----------------------------------------------------------------
// 型定義
// ----------------------------------------------------------------

export interface RelatedFollow {
  id: number;
  followNo: string;
  followDate: string;    // YYYY-MM-DD
  companyName: string;
  customerName: string;
  assignee: string;
  content: string;
  attachments: { id: number; name: string; size: string }[];
  comments: FollowComment[];
}

export interface RelatedFollowModalProps {
  open: boolean;
  onCancel: () => void;
  /** 関連フォロー一覧（親がAPIから取得して渡す） */
  follows: RelatedFollow[];
  /** 一覧のローディング状態 */
  loading?: boolean;
  /** 表示対象（アンカー）のフォローNo。選択中がこれと一致する場合は「自分」の切り離しとして扱う */
  anchorFollowNo?: number | null;
  /** 担当者候補一覧（紐づけ検索フィルタ等に使用） */
  contactUsers: { userno: number; username: string; deptname: string }[];
  /** 他フォローの個別切り離し（親が cutFollow API を実行し再取得する。モーダルは開いたまま） */
  onDetachFollow?: (follow: RelatedFollow) => void | Promise<void>;
  /** 自分（アンカー）の切り離し（親が cutFollow API を実行しモーダルを閉じる。Web版 btnCutself 相当） */
  onDetachSelf?: (follow: RelatedFollow) => void | Promise<void>;
  /** 全件切り離し（親が cutFollowAll API を実行しモーダルを閉じる。Web版 btnCut 相当） */
  onDetachAll?: () => void | Promise<void>;
  /** フォロー紐づけ追加（親が addFollowLink API を実行し再取得する） */
  onAddLink?: (payload: { followNos: number[]; groupNos: number[] }) => void | Promise<void>;
  /** 紐づけモーダルの初期検索シード（紐づけ元の会社名/顧客名） */
  initialCompanyName?: string;
  initialCustomerName?: string;
  /** 紐づけ対象フォローの検索（内部の LinkFollowModal に委譲） */
  onSearchLinkable: (params: {
    companyName?: string;
    customerName?: string;
    assigneeUserno?: number | null;
  }) => Promise<any[]>;
  /** 添付ファイルのプレビュー（新しいタブで表示） */
  onPreviewAttachment?: (attachment: { id: number; name: string }) => void;
  /** 添付ファイルのダウンロード */
  onDownloadAttachment?: (attachment: { id: number; name: string }) => void;
  /** 現在表示中（選択中）の関連フォローの followNo が変わったときの通知（親の useFollowComments 駆動用） */
  onCurrentFollowChange?: (followNo: number | null) => void;
  /** コメント追加ボタン押下時 */
  onAddComment?: () => void;
  /** コメント編集ボタン押下時 */
  onEditComment?: (comment: FollowComment) => void;
  /** コメント削除ボタン押下時 */
  onDeleteComment?: (comment: FollowComment) => void;
  /** ログインユーザーのuserno（コメントの本人編集/削除判定に使用。クライアント側チェックのみ） */
  currentUserNo?: number | null;
  zIndex?: number;
}

// ----------------------------------------------------------------
// コンポーネント
// ----------------------------------------------------------------

const RelatedFollowModal: React.FC<RelatedFollowModalProps> = ({
  open,
  onCancel,
  follows,
  loading,
  anchorFollowNo,
  contactUsers,
  onDetachFollow,
  onDetachSelf,
  onDetachAll,
  onAddLink,
  onSearchLinkable,
  onPreviewAttachment,
  onDownloadAttachment,
  onCurrentFollowChange,
  onAddComment,
  onEditComment,
  onDeleteComment,
  currentUserNo,
  initialCompanyName,
  initialCustomerName,
  zIndex = MODAL_Z.L2,
}) => {
  // 確認ダイアログ / 内部のフォロー紐づけモーダル（L3相当）の zIndex
  const subZIndex = zIndex + 10;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [detachConfirmVisible, setDetachConfirmVisible] = useState(false);
  const [detachAllConfirmVisible, setDetachAllConfirmVisible] = useState(false);
  const [linkFollowVisible, setLinkFollowVisible] = useState(false);
  const [detaching, setDetaching] = useState(false);

  // モーダルを開くたびに先頭にリセット
  useEffect(() => {
    if (open) setSelectedIndex(0);
  }, [open]);

  // 選択チップを中央スクロール
  useEffect(() => {
    chipRefs.current[selectedIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [selectedIndex]);

  const total = follows.length;
  const current = follows[selectedIndex] ?? null;
  // 選択中が表示対象（アンカー）自身か。自分の切り離しはモーダルを閉じる挙動にする（Web版準拠）
  const isCurrentSelf = current != null && anchorFollowNo != null && current.id === anchorFollowNo;

  // 選択中の関連フォローが変わるたびに親へ通知（親側の useFollowComments を追従させる）
  useEffect(() => {
    onCurrentFollowChange?.(open ? (current?.id ?? null) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, current?.id]);

  const handlePrev = () => setSelectedIndex(i => Math.max(0, i - 1));
  const handleNext = () => setSelectedIndex(i => Math.min(total - 1, i + 1));

  return (
    <>
    {open && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: zIndex - 1 }} />
    )}
    <Modal
      title={`関連フォロー（${total}件）`}
      open={open}
      onCancel={onCancel}
      width={520}
      centered
      zIndex={zIndex}
      mask={false}
      styles={{
        header: { padding: 0, marginBottom: 0 },
        body: { padding: 0, display: 'flex', flexDirection: 'column', maxHeight: '75vh' },
      }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          {/* 左: 破壊的操作 */}
          <Button
            danger
            size="small"
            icon={<DisconnectOutlined />}
            disabled={!!loading || total === 0}
            onClick={() => setDetachAllConfirmVisible(true)}
          >
            すべて切り離す
          </Button>
          {/* 右: 追加 / 閉じる */}
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => { setLinkFollowVisible(true); }}
          >
            フォローを追加
          </Button>
        </div>
      }
    >
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : total === 0 ? (
        <Empty description="関連フォローはありません" style={{ padding: 40 }} />
      ) : (
        <>
          {/* ── タイムラインナビゲーション（スクロール外・固定表示） ── */}
          <div style={{ flexShrink: 0, padding: '12px 0', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* 前へ */}
              <Button
                size="small"
                icon={<LeftOutlined style={{ fontSize: 11 }} />}
                disabled={selectedIndex === 0}
                onClick={handlePrev}
                style={{ flexShrink: 0, width: 20, minWidth: 20, height: 36, padding: 0, borderRadius: 6 }}
              />

              {/* スクロール可能なチップ列 */}
              <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 0, minWidth: 'max-content', padding: '2px 4px' }}>
                  {follows.map((f, i) => (
                    <div key={f.id} style={{ display: 'flex', alignItems: 'center' }}>
                      {/* チップ間の接続線 */}
                      {i > 0 && (
                        <div style={{
                          width: 10,
                          height: 2,
                          background: i <= selectedIndex ? '#1677ff' : '#e0e0e0',
                          flexShrink: 0,
                          transition: 'background 0.2s',
                        }} />
                      )}
                      {/* チップ（番号＋日付の2段表示） */}
                      <div
                        ref={el => { chipRefs.current[i] = el; }}
                        onClick={() => setSelectedIndex(i)}
                        style={{
                          minWidth: 36,
                          height: 36,
                          borderRadius: 8,
                          background: i === selectedIndex ? '#1677ff' : '#fff',
                          border: `2px solid ${i === selectedIndex ? '#1677ff' : '#d9d9d9'}`,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0 4px',
                          gap: 1,
                          transition: 'all 0.2s',
                          flexShrink: 0,
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        <span style={{
                          fontSize: 11,
                          fontWeight: 'bold',
                          color: i === selectedIndex ? '#fff' : '#8c8c8c',
                          lineHeight: 1,
                        }}>
                          {i + 1}
                        </span>
                        <span style={{
                          fontSize: 9,
                          color: i === selectedIndex ? 'rgba(255,255,255,0.85)' : '#bfbfbf',
                          lineHeight: 1,
                          whiteSpace: 'nowrap',
                        }}>
                          {f.followDate.slice(5).replace('-', '/')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 次へ */}
              <Button
                size="small"
                icon={<RightOutlined style={{ fontSize: 11 }} />}
                disabled={selectedIndex === total - 1}
                onClick={handleNext}
                style={{ flexShrink: 0, width: 20, minWidth: 20, height: 36, padding: 0, borderRadius: 6 }}
              />

              {/* ページ表示（インライン） */}
              <span style={{ flexShrink: 0, fontSize: 11, color: '#8c8c8c', marginLeft: 4, whiteSpace: 'nowrap' }}>
                {selectedIndex + 1} / {total}
              </span>
            </div>
          </div>

          {/* ── 選択中フォローの詳細（スクロール対象） ── */}
          {current && (
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {/* ヘッダー情報 */}
              <div style={{ background: '#fafafa', padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CalendarOutlined style={{ color: '#8c8c8c', fontSize: 13, flexShrink: 0 }} />
                    <Text style={{ fontSize: 13 }}>{current.followDate}</Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <BankOutlined style={{ color: '#8c8c8c', fontSize: 13, flexShrink: 0 }} />
                    <Text style={{ fontSize: 13 }}>{current.companyName}</Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <UserOutlined style={{ color: '#8c8c8c', fontSize: 13, flexShrink: 0 }} />
                    <Text style={{ fontSize: 13 }}>{current.customerName}</Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <UserOutlined style={{ color: '#8c8c8c', fontSize: 13, flexShrink: 0 }} />
                    <Text style={{ fontSize: 13, color: '#595959' }}>担当: {current.assignee}</Text>
                  </div>
                </div>
              </div>

              {/* フォロー内容 */}
              <div style={{ padding: '14px 16px', background: '#e6f4ff' }}>
                <div style={{ fontSize: 11, fontWeight: 'bold', color: '#4096ff', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>フォロー内容</div>
                <div style={{ fontSize: 14, color: '#262626', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                  {current.content}
                </div>
              </div>

              {/* コメント */}
              <Divider style={{ margin: 0 }} />
              <div style={{ padding: '12px 16px' }}>
                <CommentSection
                  comments={current.comments}
                  currentUserNo={currentUserNo}
                  onAdd={onAddComment}
                  onEdit={onEditComment}
                  onDelete={onDeleteComment}
                  showCount
                />
              </div>

              {/* 添付ファイル（0件の場合は非表示） */}
              {current.attachments.length > 0 && (
                <>
                  <Divider style={{ margin: 0 }} />
                  <div style={{ padding: '12px 16px' }}>
                    <div style={{ fontSize: 11, fontWeight: 'bold', color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      添付ファイル（{current.attachments.length}件）
                    </div>
                    {current.attachments.map(a => (
                      <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 13 }}>
                        <PaperClipOutlined style={{ color: '#8c8c8c', flexShrink: 0 }} />
                        <span
                          style={{ flex: 1, color: '#1677ff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: onPreviewAttachment ? 'pointer' : 'default' }}
                          onClick={() => onPreviewAttachment?.({ id: a.id, name: a.name })}
                        >
                          {a.name}
                        </span>
                        <span style={{ color: '#8c8c8c', fontSize: 11, flexShrink: 0 }}>{a.size}</span>
                        {onDownloadAttachment && (
                          <Button
                            type="text"
                            size="small"
                            icon={<DownloadOutlined />}
                            style={{ flexShrink: 0, padding: '0 4px' }}
                            onClick={() => onDownloadAttachment({ id: a.id, name: a.name })}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* アクションボタン */}
              <Divider style={{ margin: 0 }} />
              <div style={{ padding: '12px 16px', display: 'flex', gap: 8, flexWrap: 'wrap', background: '#fafafa' }}>
                <Button
                  size="small"
                  danger
                  icon={<DisconnectOutlined />}
                  onClick={() => setDetachConfirmVisible(true)}
                >
                  {isCurrentSelf ? '切り離して閉じる' : 'このフォローを切り離す'}
                </Button>
                {/* TODO: 関連フォローの編集（保留中）
                     安全に編集するには対象フォローの詳細（オプション項目・同席者等）を
                     selectFollow でロードしてから FollowFormModal を開く必要がある。
                     本実装時は親の編集フロー（convertFollowResponseToFormValues + registerFollow）を
                     共有ヘルパー化し、編集ボタンとモーダルをここに追加すること。 */}
              </div>
            </div>
          )}
        </>
      )}
    </Modal>

    {/* 切り離し確認モーダル */}
    {detachConfirmVisible && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: subZIndex - 1 }} />
    )}
    <Modal
      title="関連フォローの切り離し"
      open={detachConfirmVisible}
      onCancel={() => { if (!detaching) setDetachConfirmVisible(false); }}
      centered
      closable={!detaching}
      maskClosable={!detaching}
      mask={false}
      zIndex={subZIndex}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button disabled={detaching} onClick={() => setDetachConfirmVisible(false)}>キャンセル</Button>
          <Button
            danger
            type="primary"
            loading={detaching}
            disabled={detaching}
            onClick={async () => {
              // 自分（アンカー）は onDetachSelf（親が cutFollow＋モーダルを閉じる）、
              // 他フォローは onDetachFollow（親が cutFollow＋再取得、開いたまま）
              setDetaching(true);
              try {
                if (current) {
                  if (isCurrentSelf) {
                    await onDetachSelf?.(current);
                  } else {
                    await onDetachFollow?.(current);
                  }
                }
                setDetachConfirmVisible(false);
                setSelectedIndex(0);
              } finally {
                setDetaching(false);
              }
            }}
          >
            切り離す
          </Button>
        </div>
      }
    >
      <p>選択した関連フォローを切り離しますか？</p>
    </Modal>

    {/* すべて切り離し確認モーダル */}
    {detachAllConfirmVisible && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: subZIndex - 1 }} />
    )}
    <Modal
      title="すべての関連フォローの切り離し"
      open={detachAllConfirmVisible}
      onCancel={() => { if (!detaching) setDetachAllConfirmVisible(false); }}
      centered
      closable={!detaching}
      maskClosable={!detaching}
      mask={false}
      zIndex={subZIndex}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button disabled={detaching} onClick={() => setDetachAllConfirmVisible(false)}>キャンセル</Button>
          <Button
            danger
            type="primary"
            loading={detaching}
            disabled={detaching}
            onClick={async () => {
              // グループ内の全フォローを一括切り離す（親が cutFollowAll API 実行＋再取得）
              setDetaching(true);
              try {
                await onDetachAll?.();
                setDetachAllConfirmVisible(false);
                setSelectedIndex(0);
              } finally {
                setDetaching(false);
              }
            }}
          >
            切り離す
          </Button>
        </div>
      }
    >
      <p>すべての関連フォローを切り離しますか？</p>
    </Modal>

    {/* フォロー紐づけモーダル */}
    <LinkFollowModal
      open={linkFollowVisible}
      onCancel={() => setLinkFollowVisible(false)}
      onLink={async (payload) => {
        setLinkFollowVisible(false);
        if (payload.followNos.length + payload.groupNos.length > 0) {
          await onAddLink?.(payload);
          // 追加により一覧が再取得・並び替えされるため、選択中チップを先頭に戻して内容のずれを防ぐ
          setSelectedIndex(0);
        }
      }}
      onSearch={onSearchLinkable}
      contactUsers={contactUsers}
      // Web版準拠: 検索条件のシードは大元のアンカーではなく、タイムラインで今表示中のフォロー（current）の
      // 会社名・顧客名を優先する（Web版 linkedFollow.js の getSelectedCustomerno 相当）。
      // 一覧が空でcurrentが無い場合のみアンカーの値にフォールバックする。
      initialCompanyName={current?.companyName ?? initialCompanyName}
      initialCustomerName={current?.customerName ?? initialCustomerName}
      zIndex={subZIndex}
    />
    </>
  );
};

export default RelatedFollowModal;
